import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ImageUploader } from '../components/upload/ImageUploader';
import { UploadProgressOverlay } from '../components/upload/UploadProgressOverlay';
import { uploadConnectorService, UploadConnector } from '../services/uploadConnector';
import { useUpload } from '../contexts/UploadContext';
import { useToast } from '../hooks/use-toast';
import {
  Loader2,
  ArrowRight,
  CheckCircle,
  Images,
  Sparkles
} from 'lucide-react';

export function UploadsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    activeUpload,
    isUploading,
    startUpload,
    skipOverlay,
    showOverlay,
    lastResult
  } = useUpload();

  const [connector, setConnector] = useState<UploadConnector | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadConnector();
  }, []);

  const loadConnector = async () => {
    try {
      setIsLoading(true);
      const defaultConnector = await uploadConnectorService.getDefaultConnector();
      setConnector(defaultConnector);
    } catch (err: any) {
      console.error('Failed to load connector:', err);
      toast({
        title: 'Error',
        description: 'Failed to initialize upload connector',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpload = async (files: File[], customBatchName?: string) => {
    if (!connector) {
      toast({
        title: 'Error',
        description: 'Upload connector not ready',
        variant: 'destructive',
      });
      return;
    }

    const batchName = customBatchName || `Upload ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`;

    try {
      const result = await startUpload(connector.id, files, batchName);
      toast({
        title: 'Upload Complete',
        description: `Successfully uploaded ${result.stats.success} images`,
      });
    } catch (err: any) {
      toast({
        title: 'Upload Failed',
        description: err.message || 'Failed to upload images',
        variant: 'destructive',
      });
    }
  };

  const handleSkip = () => {
    skipOverlay();
    toast({
      title: 'Upload Continuing',
      description: 'Your images are uploading in the background. You can continue working.',
    });
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Upload Progress Overlay */}
      <UploadProgressOverlay
        progress={activeUpload?.progress || null}
        isVisible={showOverlay && isUploading}
        filesCount={activeUpload?.filesCount || 0}
        onSkip={handleSkip}
      />

      <div className="container mx-auto px-4 py-8 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Upload Images</h1>
            <p className="text-muted-foreground mt-1">
              Upload images to process with AI transformations
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate('/assets')}>
            <Images className="w-4 h-4 mr-2" />
            View All Assets
          </Button>
        </div>

        {/* Upload Section */}
        <div className="space-y-6">
          <ImageUploader
            onUpload={handleUpload}
            maxFiles={500}
            maxFileSize={15 * 1024 * 1024}
            isUploading={isUploading}
            uploadProgress={activeUpload?.progress?.percentage || 0}
          />

          {/* Success Card */}
          {lastResult && !isUploading && (
            <Card className="border-emerald-500/30 bg-gradient-to-r from-emerald-500/5 to-transparent overflow-hidden">
              <CardContent className="pt-6 relative">
                {/* Background decoration */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl" />

                <div className="flex items-center justify-between relative">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-emerald-500/20 rounded-2xl flex items-center justify-center">
                      <CheckCircle className="w-7 h-7 text-emerald-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        Upload Successful!
                        <Sparkles className="w-4 h-4 text-emerald-500" />
                      </h3>
                      <p className="text-muted-foreground">
                        {lastResult.stats.success} images are ready for transformation
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/assets?batch=${lastResult.batchId}`)}
                      className="border-emerald-500/30 hover:border-emerald-500/50"
                    >
                      View Batch
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                    <Button
                      onClick={() => navigate('/assets')}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      Go to Assets
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
